import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { noop, useConfig } from '@travelwin/core';
import Cookies from 'js-cookie';
import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { CURRENCY_STORAGE_KEY } from '../../constants';
import { getCookieDomain } from '../../utils/urlUtils';

export const CurrencyContext = createContext<{
  /**
   * We have to distinguish absence of currency in 2 situations due to:
   * - not initialised inside `initCurrency` (right after component mount),
   * - `initCurrency` has run, but currency is not set yet.
   * Hence, first situation is indicated by `undefined` and second by `null`.
   */
  currency: string | undefined | null;
  setCurrency: (newValue: string) => void;
}>({
  currency: undefined,
  setCurrency: noop,
});

const CurrencyCookies = Cookies.withAttributes({
  expires: 365,
  domain: getCookieDomain(),
});

export const CurrencyProvider = ({
  children,
  ssrCurrency,
}: PropsWithChildren<{ ssrCurrency?: string }>) => {
  const { PAYMENT_DEFAULT_CURRENCY, PAYMENT_SUPPORTED_CURRENCIES } =
    useConfig();

  const selectedCurrency = PAYMENT_SUPPORTED_CURRENCIES?.length
    ? PAYMENT_DEFAULT_CURRENCY ?? PAYMENT_SUPPORTED_CURRENCIES[0]
    : undefined;

  const [currency, setCurrency] = useState<string | undefined | null>(
    ssrCurrency ?? undefined,
  );

  useEffect(() => {
    const initCurrency = async () => {
      if (Capacitor.isNativePlatform()) {
        const { value } = await Preferences.get({ key: CURRENCY_STORAGE_KEY });
        setCurrency(value ?? selectedCurrency);
      } else {
        setCurrency(
          CurrencyCookies.get(CURRENCY_STORAGE_KEY) ?? selectedCurrency,
        );
      }
    };

    initCurrency();
  }, []);

  useEffect(() => {
    const saveCurrency = async (newValue: string) => {
      if (Capacitor.isNativePlatform()) {
        await Preferences.set({ key: CURRENCY_STORAGE_KEY, value: newValue });
      } else {
        CurrencyCookies.set(CURRENCY_STORAGE_KEY, newValue);
      }
    };

    if (currency) {
      saveCurrency(currency);
    }
  }, [currency]);

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
    }),
    [currency],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
